import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { SEO } from './index';
import { Header, CaseStudy, Footer } from './sections';
import { mapPostToCaseStudy } from '../lib/caseStudyMapping';
import { useLanguageSwitcher } from '../contexts/LanguageSwitcherContext';
import { WORK_FI } from '../lib/blogPaths';
import { getCanonicalUrl, buildHreflangTags } from '../lib/siteUrl';

/**
 * Renders the case study layout (CaseStudy + Footer) with post and ACF data.
 * Used by both the FaustWP single template and the /tyomme/[slug] and /en/work/[slug] pages.
 */
export function CaseStudyPage({ post, generalSettings, lang = 'fi', rawCaseStudy = null, fiSlug = null, enSlug = null, canonicalPath = null }) {
  const router = useRouter();
  const { setLanguageSwitcherUrls } = useLanguageSwitcher();
  const { title: siteTitle, description: siteDescription } = generalSettings ?? {};
  const postTitle = post?.title;
  const imageUrl =
    post?.customerStory?.headerImage?.node?.sourceUrl ??
    post?.featuredImage?.node?.sourceUrl;
  const rawAcf = rawCaseStudy ?? post?.customerStory ?? post?.caseStudy;
  const caseStudy = mapPostToCaseStudy(rawAcf);
  const canonicalUrl = canonicalPath ? getCanonicalUrl(canonicalPath) : undefined;
  const seoTitleOverride = (post?.seoFields?.seoTitle ?? '').trim();
  const seoDescriptionOverride = (post?.seoFields?.seoDescription ?? '').trim();
  const defaultSeoTitle = postTitle ? `${postTitle} | ${siteTitle}` : siteTitle;
  const seoTitle = seoTitleOverride || defaultSeoTitle;
  const seoDescription = seoDescriptionOverride || siteDescription;
  const slug = post?.slug;
  const fiPath = fiSlug ? `/tyomme/${fiSlug}` : (lang === 'fi' && slug ? `/tyomme/${slug}` : null);
  const enPath = enSlug ? `/work/${enSlug}` : (lang === 'en' && slug ? `/work/${slug}` : null);
  const hreflangTags = fiPath ? buildHreflangTags(fiPath, enPath) : undefined;

  useEffect(() => {
    const asPath = router?.asPath ?? '';
    if (lang === 'en') {
      setLanguageSwitcherUrls({
        fiUrl: fiSlug ? `${WORK_FI}/${fiSlug}` : WORK_FI,
        enUrl: asPath || '/en/work',
      });
    } else {
      setLanguageSwitcherUrls({
        fiUrl: asPath || WORK_FI,
        enUrl: enSlug ? `/en/work/${enSlug}` : '/en/work',
      });
    }
  }, [lang, fiSlug, enSlug, router?.asPath, setLanguageSwitcherUrls]);

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        imageUrl={imageUrl}
        url={canonicalUrl}
        alternates={hreflangTags}
      />
      <div className="min-h-screen bg-white">
        <Header />
        <CaseStudy
          post={{ title: postTitle, featuredImage: imageUrl, slug: post?.slug }}
          caseStudy={caseStudy}
          lang={lang}
          slug={post?.slug}
          fiSlug={fiSlug}
          enSlug={enSlug}
        />
        <Footer />
      </div>
    </>
  );
}
